import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@krea/shared/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary-150 text-primary shadow-sm",
        success: "border-transparent bg-mint-500 text-white shadow-sm",
        secondary: "border-transparent bg-gray-100 text-black",
        destructive: "border-transparent bg-red-100 text-black shadow-sm",
        outline: "text-black",
        // orange-200, not orange-50: `orange-50` is not a brand token, so it resolved to Tailwind
        // v4's default palette — an oklch() value. That both broke the token rule and put an
        // oklch colour in a shared component, which html2canvas 1.4.1 cannot parse (see
        // the oklch "Don't" in DESIGN.md — it silently kills the lender PDF export). Black on the
        // tint also fixes the contrast: orange-on-orange-50 measured ~2.2:1, well under AA.
        warning: "border-transparent bg-orange-200 text-black shadow-sm",
        count:
          "border-transparent bg-orange px-2 py-0.5 text-xs font-bold leading-none text-white! shadow-sm", // typography: font-bold exception — max visual pop at xs size; documented in TYPOGRAPHY.md §3
        successLight: "border-transparent bg-mint-100 text-mint-500 shadow-sm",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
